export interface CompletionPayload {transcript?:unknown[];integrity_events?:unknown[];integrity_score?:number}
export interface CompletionReceipt {accepted:true;isCurrent:()=>boolean}
export class CompletionUnconfirmedError extends Error {constructor(){super('Completion has not been confirmed.');}}
export class CompletionScopeChangedError extends Error {constructor(){super('Completion scope changed.');}}

// This identity is a local cancellation fence, never an authorization decision.
// The API still verifies the JWT, selected tenant and owning session.
export function completionScope(token:string|null,group:string|null,session:string|undefined):string|null {
  if(!token||!session)return null;
  try {
    const claims=JSON.parse(atob(token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
    if(typeof claims.id!=='string'||!claims.id.trim()||(claims.type!==undefined&&claims.type!=='access'))return null;
    return JSON.stringify([claims.id,group??claims.active_group_id??claims.primary_group_id??null,session]);
  } catch {return null;}
}
type Dependencies={
  scope:()=>string|null;
  complete:(id:string,payload:CompletionPayload,signal:AbortSignal)=>Promise<unknown>;
  status:(id:string,signal:AbortSignal)=>Promise<unknown>;
  timeoutMs?:number;
};
type Pending={scope:string;id:string;serialized:string;epoch:number;attempted:boolean;readOnly:boolean;inFlight:Promise<CompletionReceipt>|null;receipt:CompletionReceipt|null};
export class CandidateCompletionController {
  private pending:Pending|null=null;
  private epoch=0;
  private controllers=new Set<AbortController>();
  constructor(private readonly deps:Dependencies){}
  isLocked(id:string|undefined){return Boolean(this.pending&&this.pending.id===id&&this.current(this.pending));}
  invalidate(){this.epoch++;if(this.pending)this.pending.serialized='';this.pending=null;for(const controller of this.controllers)controller.abort();this.controllers.clear();}
  private current(p:Pending){return this.pending===p&&this.epoch===p.epoch&&this.deps.scope()===p.scope;}
  private assertCurrent(p:Pending){if(!this.current(p)){if(this.pending===p)this.invalidate();throw new CompletionScopeChangedError();}}
  private async request(p:Pending,work:(signal:AbortSignal)=>Promise<unknown>){
    this.assertCurrent(p);const controller=new AbortController();this.controllers.add(controller);let timer:ReturnType<typeof setTimeout>|undefined;let abort:()=>void=()=>{};
    try {
      const result=await Promise.race([work(controller.signal),new Promise<never>((_,reject)=>{
        abort=()=>reject(new CompletionUnconfirmedError());controller.signal.addEventListener('abort',abort,{once:true});
        timer=setTimeout(()=>controller.abort(),this.deps.timeoutMs??15000);
      })]);
      this.assertCurrent(p);return result;
    } finally {if(timer)clearTimeout(timer);controller.signal.removeEventListener('abort',abort);this.controllers.delete(controller);}
  }
  private accepted(p:Pending):CompletionReceipt {
    this.assertCurrent(p);p.serialized='';p.receipt??={accepted:true,isCurrent:()=>this.current(p)};return p.receipt;
  }
  private async read(p:Pending):Promise<'accepted'|'not_submitted'|'unknown'>{
    try {
      const result=await this.request(p,signal=>this.deps.status(p.id,signal)) as {completion?:{accepted?:unknown;assessment?:unknown}};
      if(result?.completion?.accepted===true&&result.completion.assessment==='available')return 'accepted';
      if(result?.completion?.accepted===false&&result.completion.assessment==='not_submitted')return 'not_submitted';
      return 'unknown';
    }catch(error){this.assertCurrent(p);if(error instanceof CompletionScopeChangedError)throw error;return 'unknown';}
  }
  confirm(id:string,payload:CompletionPayload,readOnly=false):Promise<CompletionReceipt>{
    const scope=this.deps.scope();if(!scope||!id)return Promise.reject(new CompletionScopeChangedError());
    if(this.pending && (this.pending.scope!==scope||this.pending.id!==id))this.invalidate();
    if(!this.pending){let serialized:string;try{serialized=readOnly?'':JSON.stringify(payload);}catch{return Promise.reject(new CompletionUnconfirmedError());}this.pending={scope,id,serialized,epoch:this.epoch,attempted:readOnly,readOnly,inFlight:null,receipt:null};}
    const p=this.pending;if(p.receipt)return Promise.resolve(p.receipt);if(p.inFlight)return p.inFlight;
    const execute=async()=>{
      this.assertCurrent(p);
      if(p.attempted){const status=await this.read(p);if(status==='accepted')return this.accepted(p);if(status!=='not_submitted'||p.readOnly||readOnly)throw new CompletionUnconfirmedError();}
      p.attempted=true;
      try {
        const result=await this.request(p,signal=>this.deps.complete(p.id,JSON.parse(p.serialized),signal)) as {assessment_id?:unknown;session?:{id?:unknown;status?:unknown}};
        if(typeof result?.assessment_id==='string'&&result.assessment_id.trim()&&result.session?.id===p.id&&result.session.status==='completed')return this.accepted(p);
      } catch {this.assertCurrent(p);}
      if(await this.read(p)==='accepted')return this.accepted(p);
      throw new CompletionUnconfirmedError();
    };
    p.inFlight=Promise.resolve().then(execute).finally(()=>{p.inFlight=null;});return p.inFlight;
  }
}
